import React from "react";
import { useServiceT } from "@/utils/hooks/useServiceT";
import cls from "./styles.module.scss";

const SECTORS = [4, 8, 12, 16, 20, 24];
const CENTER = 200;
const RADIUS = 188;
const LABEL_RADIUS = 136;
const SELECTED_SECTOR_INDEX = 4;

const polarToCartesian = (angle: number, radius: number) => {
  const radians = ((angle - 90) * Math.PI) / 180;

  return {
    x: CENTER + radius * Math.cos(radians),
    y: CENTER + radius * Math.sin(radians),
  };
};

const createSectorPath = (startAngle: number, endAngle: number) => {
  const start = polarToCartesian(startAngle, RADIUS);
  const end = polarToCartesian(endAngle, RADIUS);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${CENTER} ${CENTER}`,
    `L ${start.x} ${start.y}`,
    `A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

export const WheelPreview = () => {
  const serviceT = useServiceT();
  const sectorAngle = 360 / SECTORS.length;

  return (
    <div className={cls.viewport} aria-hidden>
      <div className={cls.wheel}>
        <svg className={cls.svg} viewBox="0 0 400 400">
          <defs>
            <filter id="previewSectorInnerShadow">
              <feOffset dx="0" dy="8" />
              <feGaussianBlur stdDeviation="10" result="offsetBlur" />
              <feComposite
                in="offsetBlur"
                in2="SourceAlpha"
                operator="arithmetic"
                k2="-1"
                k3="1"
                result="innerShadow"
              />
              <feColorMatrix
                in="innerShadow"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.42 0"
              />
              <feComposite in2="SourceGraphic" operator="over" />
            </filter>
          </defs>

          <g className={cls.sectors}>
            {SECTORS.map((number, index) => {
              const startAngle = index * sectorAngle;
              const endAngle = startAngle + sectorAngle;
              const midAngle = startAngle + sectorAngle / 2;
              const labelPoint = polarToCartesian(midAngle, LABEL_RADIUS);
              const isSelected = index === SELECTED_SECTOR_INDEX;

              return (
                <g key={number}>
                  <path
                    className={[
                      index % 2 === 0 ? cls.redSector : cls.greenSector,
                      isSelected ? cls.selectedSector : "",
                    ].join(" ")}
                    d={createSectorPath(startAngle, endAngle)}
                    filter="url(#previewSectorInnerShadow)"
                  />
                  <text
                    className={cls.label}
                    x={labelPoint.x}
                    y={labelPoint.y}
                    transform={`rotate(${midAngle} ${labelPoint.x} ${labelPoint.y})`}
                  >
                    <tspan className={cls.labelNumber} x={labelPoint.x} dy="">
                      {number}
                    </tspan>
                    <tspan
                      className={cls.labelCaption}
                      x={labelPoint.x}
                      dy="1.25em"
                    >
                      {serviceT("goals.common.rides", { count: number })}
                    </tspan>
                  </text>
                </g>
              );
            })}
          </g>
          <image
            className={cls.centerPin}
            href="/goals/wheelCenter.webp"
            x="138"
            y="138"
            width="124"
            height="124"
            preserveAspectRatio="xMidYMid meet"
          />
        </svg>
      </div>
    </div>
  );
};
